import { getBase64ImageByUrl } from '~src/utils';
import { canvasJsonToImage } from '~core';
import { getState } from '~src/state';
import { resizeDimensions } from '~loader/loader-utils';
import {
  calculateFormat,
  calculateFormatDpi,
  isPositiveFiniteNumber,
} from '~loader/loader-utils/calculateFormat';
import {
  ensureCurrentPageImage,
  isCurrentPage,
  PageImageError,
} from '~src/multipages/currentPageImage';

// Format sizes are rounded to whole pixels on both sides.
const PREVIEW_SIZE_TOLERANCE_PX = 2;

// Print size of the selected format, resolved like the editor canvas.
export const getPageExportSize = () => {
  const { formatWidth, formatHeight } = calculateFormat();
  const dpi = calculateFormatDpi(
    {
      editor_project_formats_pointer: getState('selected.formato'),
      editor_project_formats_default_pointer: getState(
        'selected.formatoDefault'
      ),
    },
    window.editorLoader?.sourceDPI
  );
  const defaultDpi = window.editorLoader?.defaultDPI;

  if (
    ![formatWidth, formatHeight, dpi, defaultDpi].every(isPositiveFiniteNumber)
  ) {
    return null;
  }

  return resizeDimensions(formatWidth, formatHeight, defaultDpi, dpi);
};

// Saved previews above the renderer pixel cap (lambda-image-handler) are
// smaller than the page: export and print need the full size.
const getMinimumPreviewSize = () => {
  let size = null;
  try {
    size = getPageExportSize();
  } catch (err) {
    console.warn('Cannot resolve page export size', err);
  }

  if (!size) {
    return {};
  }

  return {
    minWidth: size.width - PREVIEW_SIZE_TOLERANCE_PX,
    minHeight: size.height - PREVIEW_SIZE_TOLERANCE_PX,
  };
};

export const getImageFromPage = async (page) => {
  // La pagina aperta può essere cambiata dopo l'ultima generazione: la sua
  // immagine si aggiorna qui, e se non si riesce l'esportazione si ferma.
  if (isCurrentPage(page)) {
    try {
      return ensureCurrentPageImage();
    } catch (error) {
      throw new PageImageError(error);
    }
  }

  let image;

  // imgDataDirty resta true se l'immagine non si è potuta generare lasciando
  // la pagina: imgData e l'anteprima salvata sono vecchie, vale canvasJson.
  if (page.imgData && !page.imgDataDirty) {
    // For visited pages, imgData is already a clean base64 string
    image = page.imgData;
  } else if (!page.imgDataDirty && page.preview && page.preview.url) {
    try {
      // Use JPEG format to match what the PDF expects
      image = await getBase64ImageByUrl(
        page.preview.url,
        'image/jpeg',
        0.92,
        getMinimumPreviewSize()
      );
    } catch (err) {
      console.warn('Cannot use saved preview, generating at runtime', err);
      const generated = await canvasJsonToImage(page.canvasJson);
      // Ensure we return just the base64 string, not the full object
      image = generated.base64;
    }
  } else {
    const generated = await canvasJsonToImage(page.canvasJson);
    image = generated.base64;
  }

  // Ensure the image is a clean base64 string without data URL prefix
  if (image && image.includes('data:')) {
    image = image.split(',')[1];
  }

  // Le esportazioni successive (anche l'anteprima del provino) la riusano.
  page.imgData = image;
  page.imgDataDirty = false;

  return image;
};
